import {
  SurveyQuotaSupplier,
  SurveyQuotaSupplierTarget,
} from '../models/survey-quota.model';

/**
 * Applies the update-current-target response's per-supplier targets to the
 * suppliers already in state.
 *
 * Left-joined on the suppliers in state: a target whose supplierRefId isn't
 * there is dropped, because the response carries no name or dates for it and
 * such a row could not be rendered. Everything the response doesn't return
 * (name, dates, valid clicks, …) is carried across from state.
 */
export function mergeQuotaSupplierTargets(
  suppliers: readonly SurveyQuotaSupplier[],
  targets: readonly SurveyQuotaSupplierTarget[],
): SurveyQuotaSupplier[] {
  const targetByRefId = new Map(
    targets.map((target) => [target.supplierRefId, target]),
  );

  return suppliers.map((supplier) => {
    const target = targetByRefId.get(supplier.supplierRefId);
    if (!target) {
      return supplier;
    }

    return {
      supplierRefId: supplier.supplierRefId,
      name: supplier.name,
      currentTarget: target.currentTarget,
      currentlyOpen: supplier.currentlyOpen,
      supplierCurrentlyOpen: supplier.supplierCurrentlyOpen,
      fielded: supplier.fielded,
      completeNeeded: target.completeNeeded,
      validClicks: supplier.validClicks,
      inProgress: supplier.inProgress,
      lastStartDate: supplier.lastStartDate,
      lastCompleteDate: supplier.lastCompleteDate,
      isPdsApplied: supplier.isPdsApplied,
      fee: supplier.fee,
    };
  });
}

/**
 * Composite key identifying one supplier's row within one quota — used to
 * track in-flight saves/pending edits for a supplier-level CT/CO patch, since
 * a psQuotaId alone isn't unique across suppliers and a supplierRefId alone
 * isn't unique across quotas.
 */
export function supplierQuotaKey(
  psQuotaId: string,
  supplierRefId: string,
): string {
  return `${psQuotaId}:${supplierRefId}`;
}

/**
 * Removes one id from an in-flight tracking array (e.g. `*SavingIds`,
 * `*LoadingIds`) once its request settles — shared by every quota handler
 * that clears an id on success/failure.
 */
export function removeId<T>(ids: readonly T[], id: T): T[] {
  return ids.filter((existingId) => existingId !== id);
}
